/**
 * ============================================================
 * src/services/jobs.service.js  –  Job Board API Service
 * ------------------------------------------------------------
 * Centralises every axios call made by the Job Board feature so
 * that endpoint URLs and request shapes are defined in one place.
 * Consuming components import named functions rather than
 * constructing URLs and headers inline.
 *
 * All functions:
 *   • Accept plain JS arguments (no axios config objects)
 *   • Return the raw axios Promise so callers can .then()/.catch()
 *     exactly as before
 *   • Are documented with the HTTP method, full URL, expected
 *     request payload shape and the response shape the callers rely on
 *
 * Base URL: https://api.varsanpr.com/api/misc/jobs
 * Auth:     Every request passes the result of authHeader() in the
 *           Authorization header (Bearer token from localStorage).
 * ============================================================
 */

import axios      from 'axios';
import authHeader from '@/services/auth-header';

/**
 * JOBS_BASE
 * ---------
 * Root URL for every jobs-feature endpoint.
 * Changing this one constant re-points all service calls at once.
 */
const JOBS_BASE = 'https://api.varsanpr.com/api/misc';

/* ── Metadata ─────────────────────────────────────────────────── */

/**
 * fetchMetadata
 * -------------
 * Retrieves the possible job types and stages from the API.
 * Should be called once on application load and cached (see the
 * jobs Vuex module) so every Jobs page shows server-defined values
 * instead of the hardcoded constants in constants/jobs.js.
 *
 * Endpoint: GET /jobs/metadata
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   types:   string[]   – e.g. ['New Install', 'Maintenance']
 *   stages:  string[]   – e.g. ['Created', 'In Preparation', ...]
 * }
 *
 * @returns {Promise<AxiosResponse>}
 */
export function fetchMetadata() {
  return axios.get(`${JOBS_BASE}/jobs/metadata`, { headers: authHeader() });
}

/* ── Jobs ────────────────────────────────────────────────────── */

/**
 * fetchJobs
 * ---------
 * Loads all jobs from the API.  The response is ordered by
 * priority (highest first) and then by created_at (oldest first).
 * Optionally filtered to a single site via a query param.
 *
 * Endpoint: GET jobs
 * Params:   site_id? {number} – when supplied, only jobs belonging
 *                               to that site are returned
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   jobs: Job[]          – see GET "/" schema for the full Job shape
 * }
 *
 * @param {number|null} siteId – site to filter by, or null for all
 * @returns {Promise<AxiosResponse>}
 */
export function fetchJobs(siteId = null) {
  const params = {};
  if (siteId) params.site_id = siteId;
  return axios.get(`${JOBS_BASE}/jobs`, { headers: authHeader(), params });
}

/**
 * fetchTrainingItems
 * ------------------
 * Returns the active training items list for the jobs board.
 * Supports the same common query-parameter pattern used by the
 * other jobs endpoints, such as site_id when the board is scoped
 * to a specific site.
 *
 * Endpoint: GET /jobs/training-items
 * Params:   site_id? {number}
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   trainingItems: TrainingItem[]
 * }
 *
 * @param {Object} params – optional query parameters
 * @returns {Promise<AxiosResponse>}
 */
export function fetchTrainingItems(params = {}) {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return axios.get(`${JOBS_BASE}/jobs/training-items`, { headers: authHeader(), params: cleaned });
}

/**
 * createTrainingItem
 * ------------------
 * Creates a new training item.
 *
 * Endpoint: PUT /jobs/training-items
 * Payload:  {
 *   job_id: number|null,
 *   site_id: number,
 *   title: string,
 *   description?: string|null,
 *   scheduled_date?: string|null
 * }
 * Headers: Authorization (Bearer)
 * Response: { success: boolean, message: string }
 *
 * @param {Object} payload – training item payload
 * @returns {Promise<AxiosResponse>}
 */
export function createTrainingItem(payload) {
  return axios.put(`${JOBS_BASE}/jobs/training-items`, payload, { headers: authHeader() });
}

/**
 * updateTrainingItem
 * ------------------
 * Updates an existing training item.
 *
 * Endpoint: PATCH /jobs/training-items/:id
 * Payload:  {
 *   site_id: number,
 *   title: string,
 *   job_id?: number|null,
 *   description?: string|null,
 *   scheduled_date?: string|null
 * }
 * Headers: Authorization (Bearer)
 * Response: { success: boolean, message: string }
 *
 * @param {number} trainingItemId – id of the training item to update
 * @param {Object} payload        – updated training item payload
 * @returns {Promise<AxiosResponse>}
 */
export function updateTrainingItem(trainingItemId, payload) {
  return axios.patch(`${JOBS_BASE}/jobs/training-items/${trainingItemId}`, payload, { headers: authHeader() });
}

/**
 * completeTrainingItem
 * --------------------
 * Marks a training item as completed.
 *
 * Endpoint: POST /jobs/training-items/:id/complete
 * Headers: Authorization (Bearer)
 * Response: { success: boolean, message: string }
 *
 * @param {number} trainingItemId – id of the training item to complete
 * @returns {Promise<AxiosResponse>}
 */
export function completeTrainingItem(trainingItemId) {
  return axios.post(`${JOBS_BASE}/jobs/training-items/${trainingItemId}/complete`, {}, { headers: authHeader() });
}

/**
 * deleteTrainingItem
 * ------------------
 * Deletes a training item.
 *
 * Endpoint: DELETE /jobs/training-items/:id
 * Headers: Authorization (Bearer)
 * Response: { success: boolean, message: string }
 *
 * @param {number} trainingItemId – id of the training item to delete
 * @returns {Promise<AxiosResponse>}
 */
export function deleteTrainingItem(trainingItemId) {
  return axios.delete(`${JOBS_BASE}/jobs/training-items/${trainingItemId}`, { headers: authHeader() });
}

/**
 * createJob
 * ---------
 * Creates a new job.
 *
 * Endpoint: PUT /jobs
 * Payload (all fields required unless noted):
 *   site_id     {integer}        required
 *   title       {string}         required
 *   description {string}         optional
 *   priority    {integer, ≥1}    required
 *   type        {string}         one of JOB_TYPES  (required)
 *   stage       {string}         one of JOB_STAGE_KEYS  (required)
 *   needs_training {boolean}     optional (default false)
 *   jr_ids      {Array}          required  – [{ jr_id: string, party_name: string }]
 *   checklists  {Array}          required  – [{ position, name, description?, stage }]
 *   due_dates   {Array}          required  – [{ due_date: ISO datetime,
 *                                                is_overall_due_date: boolean,
 *                                                stage?: string }]
 * Response: 201 { success: boolean, message: string }
 *
 * @param {Object} payload – job fields matching the schema above
 * @returns {Promise<AxiosResponse>}
 */
export function createJob(payload) {
  return axios.put(`${JOBS_BASE}/jobs`, payload, { headers: authHeader() });
}

/**
 * updateJob
 * ---------
 * Updates an existing job's fields.
 *
 * Endpoint: PATCH /jobs/:id
 * Payload:  Same shape as createJob (all fields optional for PATCH)
 * Response: { success: boolean, message: string }
 *
 * @param {number} jobId   – id of the job to update
 * @param {Object} payload – fields to update
 * @returns {Promise<AxiosResponse>}
 */
export function updateJob(jobId, payload) {
  return axios.patch(`${JOBS_BASE}/jobs/${jobId}`, payload, { headers: authHeader() });
}

/**
 * deleteJob
 * ---------
 * Soft-deletes a job (marks it as inactive in the database).
 *
 * Endpoint: DELETE /jobs/:id
 * Params:   id {integer, minimum: 1} – the unique identifier of the job to delete
 * Query:    note {string, minLength: 1} – mandatory reason for deleting the job
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string   – "Job deleted successfully" on 200
 * }
 *
 * @param {number} jobId – id of the job to delete
 * @param {string} note  – mandatory reason for the deletion
 * @returns {Promise<AxiosResponse>}
 */
export function deleteJob(jobId, note) {
  return axios.delete(
    `${JOBS_BASE}/jobs/${jobId}`,
    { headers: authHeader(), params: { note } }
  );
}

/**
 * moveJobStage
 * ------------
 * Updates only the stage of a job.  Used for drag-and-drop column
 * transitions and the inline stage selector in the detail modal.
 *
 * Endpoint: PATCH /jobs/:id/stage
 * Payload:  { stage {string} }
 * Response: { success: boolean, message: string }
 *
 * @param {number} jobId  – id of the job to move
 * @param {string} stage  – target stage key (one of JOB_STAGE_KEYS)
 * @returns {Promise<AxiosResponse>}
 */
export function moveJobStage(jobId, stage) {
  return axios.patch(
    `${JOBS_BASE}/jobs/${jobId}/stage`,
    { stage },
    { headers: authHeader() }
  );
}

/* ── Sites ───────────────────────────────────────────────────── */

/**
 * fetchJobSites
 * -------------
 * Returns the complete list of all sites for the Job Board feature.
 * Each site includes: id, name, and a pre-combined address string.
 *
 * This is intended to be called once (e.g. on component mount) and
 * stored locally.  Site search/filtering is then done by filtering
 * the cached array in the component, avoiding a network round-trip
 * on every keystroke.
 *
 * Endpoint: GET /jobs/sites
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   sites: [{ id, name, address }]
 *     address – comma-separated concatenation of address_one,
 *               address_two, address_three?, address_four?, postcode
 * }
 *
 * @returns {Promise<AxiosResponse>}
 */
export function fetchJobSites() {
  return axios.get(`${JOBS_BASE}/jobs/sites`, { headers: authHeader() });
}

/* ── Attachments ─────────────────────────────────────────────── */

/**
 * fetchAttachments
 * ----------------
 * Returns the list of file attachments for a job.
 *
 * Endpoint: GET jobs/:id/attachments
 * Params:   id {integer, minimum: 1} – the job's unique identifier
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   attachments: [{
 *     id, job_id, name, description, filename,
 *     filetype, filesize, url, created_at
 *   }]
 * }
 *
 * @param {number} jobId
 * @returns {Promise<AxiosResponse>}
 */
export function fetchAttachments(jobId) {
  return axios.get(
    `${JOBS_BASE}/jobs/${jobId}/attachments`,
    { headers: authHeader() }
  );
}

/**
 * uploadAttachment
 * ----------------
 * Uploads a file attachment to a job via PUT with multipart/form-data.
 *
 * Endpoint: PUT /jobs/:id/attachments  (multipart/form-data)
 * Params:   id {integer, minimum: 1}
 * Fields:   file        {File}   the binary file (form-data field "file")
 *           name        {string} display name
 *           description {string} optional description (nullable)
 * Response: 200 { success: boolean, message: string }
 *
 * @param {number}   jobId    – id of the job to attach to
 * @param {FormData} formData – pre-constructed FormData with file/name/description
 * @returns {Promise<AxiosResponse>}
 */
export function uploadAttachment(jobId, formData) {
  return axios.put(
    `${JOBS_BASE}/jobs/${jobId}/attachments`,
    formData,
    { headers: authHeader() }
  );
}

/**
 * deleteAttachment
 * ----------------
 * Permanently deletes a file attachment from a job.
 *
 * Endpoint: DELETE /jobs/:id/attachments/:att_id
 * Headers:  Authorization (Bearer)
 * Response: { success: boolean, message: string }
 *
 * @param {number} jobId – id of the owning job
 * @param {number} attId – id of the attachment to delete
 * @returns {Promise<AxiosResponse>}
 */
export function deleteAttachment(jobId, attId) {
  return axios.delete(
    `${JOBS_BASE}/jobs/${jobId}/attachments/${attId}`,
    { headers: authHeader() }
  );
}

/* ── Checklist ───────────────────────────────────────────────── */

/**
 * fetchChecklists
 * ---------------
 * Returns the ordered list of checklist items for a job,
 * sorted by their `position` field (1 = first item).
 *
 * Endpoint: GET /jobs/:id/checklist
 * Params:   id {integer, minimum: 1} – the job's unique identifier
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   checklist: [{
 *     id, job_id, position, name, description,
 *     stage,           – one of JOB_STAGE_KEYS or null
 *     is_completed,
 *     completion_stage,
 *     completion_notes,
 *     completed_at,
 *     completed_by
 *   }]
 * }
 *
 * @param {number} jobId
 * @returns {Promise<AxiosResponse>}
 */
export function fetchChecklists(jobId) {
  return axios.get(
    `${JOBS_BASE}/jobs/${jobId}/checklist`,
    { headers: authHeader() }
  );
}

/**
 * createChecklistItem
 * -------------------
 * Adds a new checklist item to a job.
 *
 * Endpoint: PUT /jobs/:id/checklist
 * Payload:  {
 *   name        {string}      required
 *   description {string}      optional
 *   stage       {string|null} one of JOB_STAGE_KEYS or null
 *   position    {number}      1-based display order
 * }
 * Response: { success: boolean, message: string }  (list is re-fetched)
 *
 * @param {number} jobId
 * @param {Object} payload
 * @returns {Promise<AxiosResponse>}
 */
export function createChecklistItem(jobId, payload) {
  return axios.put(
    `${JOBS_BASE}/jobs/${jobId}/checklist`,
    payload,
    { headers: authHeader() }
  );
}

/**
 * updateChecklistItem
 * -------------------
 * Updates one or more editable fields on an existing checklist item
 * (name, description, stage, position).
 *
 * Completion/toggle state is managed separately via toggleChecklistItem.
 *
 * Endpoint: PATCH /jobs/:id/checklist/:item_id
 * Payload:  Partial checklist item — at least one of:
 *   name        {string}
 *   description {string}
 *   stage       {string|null}  one of JOB_STAGE_KEYS
 *   position    {number}
 * Response: { success: boolean, message: string }
 *
 * @param {number} jobId
 * @param {number} itemId
 * @param {Object} payload
 * @returns {Promise<AxiosResponse>}
 */
export function updateChecklistItem(jobId, itemId, payload) {
  return axios.patch(
    `${JOBS_BASE}/jobs/${jobId}/checklist/${itemId}`,
    payload,
    { headers: authHeader() }
  );
}

/**
 * reorderChecklists
 * -----------------
 * Persists a new display order for all checklist items of a job.
 * Called after the user drag-and-drops a checklist row.
 *
 * Endpoint: PATCH /jobs/:id/checklist/reorder
 * Payload:  {
 *   checklistOrder: [{ id: number, position: number }, …]
 *     – one entry per item in the new order, 1-based positions
 * }
 * Response: { success: boolean, message: string }
 *
 * @param {number} jobId
 * @param {Array}  checklistOrder – [{ id, position }] for every item in new order
 * @returns {Promise<AxiosResponse>}
 */
export function reorderChecklists(jobId, checklistOrder) {
  return axios.patch(
    `${JOBS_BASE}/jobs/${jobId}/checklist/reorder`,
    { checklistOrder },
    { headers: authHeader() }
  );
}

/**
 * toggleChecklistItem
 * -------------------
 * Toggles the completion status of a checklist item.
 * Replaces the old pattern of using updateChecklistItem with completion fields.
 *
 * Endpoint: PATCH /jobs/:id/checklist/:item_id/toggle
 * Payload:  {
 *   completed      {boolean}  required – true = complete, false = un-complete
 *   completed_note {string}   optional – free-text note about the completion
 * }
 * Response: { success: boolean, message: string }
 *
 * @param {number}      jobId    – id of the owning job
 * @param {number}      itemId   – id of the checklist item to toggle
 * @param {boolean}     completed     – new completion state
 * @param {string|null} [completedNote] – optional completion note
 * @returns {Promise<AxiosResponse>}
 */
export function toggleChecklistItem(jobId, itemId, completed, completedNote = null) {
  const payload = { completed };
  if (completedNote) payload.completed_note = completedNote;
  return axios.patch(
    `${JOBS_BASE}/jobs/${jobId}/checklist/${itemId}/toggle`,
    payload,
    { headers: authHeader() }
  );
}

/**
 * deleteChecklistItem
 * -------------------
 * Permanently removes a checklist item from a job.
 *
 * Endpoint: DELETE /jobs/:id/checklist/:item_id
 * Headers:  Authorization (Bearer)
 * Response: { success: boolean, message: string }
 *
 * @param {number} jobId
 * @param {number} itemId
 * @returns {Promise<AxiosResponse>}
 */
export function deleteChecklistItem(jobId, itemId) {
  return axios.delete(
    `${JOBS_BASE}/jobs/${jobId}/checklist/${itemId}`,
    { headers: authHeader() }
  );
}

/* ── Due Dates ───────────────────────────────────────────────── */

/**
 * fetchDueDates
 * -------------
 * Returns all due dates associated with a job.  Each job can have
 * one overall due date and up to one due date per workflow stage.
 *
 * Endpoint: GET /jobs/:id/due-dates
 * Params:   id {integer, minimum: 1} – the job's unique identifier
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   due_dates: [{
 *     id,
 *     job_id,
 *     due_date           {string, format: date-time},
 *     is_overall_due_date {boolean}  – true = overall job deadline,
 *                                      false = stage-specific target
 *     stage              {string}    – the JOB_STAGE_KEY this due date
 *                                      applies to (null when is_overall)
 *   }]
 * }
 *
 * @param {number} jobId
 * @returns {Promise<AxiosResponse>}
 */
export function fetchDueDates(jobId) {
  return axios.get(
    `${JOBS_BASE}/jobs/${jobId}/due-dates`,
    { headers: authHeader() }
  );
}

/**
 * createDueDate
 * -------------
 * Adds a new due date to an existing job.
 * There can only be one entry with is_overall_due_date === true per job;
 * the API will return 400 if a second overall due date is submitted.
 *
 * Endpoint: PUT /jobs/:id/due-dates
 * Params:   id {integer, minimum: 1}
 * Payload:  {
 *   due_date            {string, ISO 8601 datetime}  required
 *   is_overall_due_date {boolean}                    required
 *   stage               {string, one of JOB_STAGE_KEYS}  optional (required when !is_overall)
 * }
 * Response: 201 { success: boolean, message: string }
 *
 * @param {number} jobId
 * @param {Object} payload – { due_date, is_overall_due_date, stage? }
 * @returns {Promise<AxiosResponse>}
 */
export function createDueDate(jobId, payload) {
  return axios.put(
    `${JOBS_BASE}/jobs/${jobId}/due-dates`,
    payload,
    { headers: authHeader() }
  );
}

/**
 * deleteDueDate
 * -------------
 * Soft-deletes a due date entry from a job (marks the record as deleted
 * in the database rather than physically removing it).
 *
 * Endpoint: DELETE /jobs/:id/due-dates/:dueDateId
 * Params:   id        {integer, minimum: 1} – the job's unique identifier
 *           dueDateId {integer, minimum: 1} – the due date entry's unique identifier
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string   – "Job due date deleted successfully" on 200
 * }
 *
 * @param {number} jobId     – id of the owning job
 * @param {number} dueDateId – id of the due date entry to delete
 * @returns {Promise<AxiosResponse>}
 */
export function deleteDueDate(jobId, dueDateId) {
  return axios.delete(
    `${JOBS_BASE}/jobs/${jobId}/due-dates/${dueDateId}`,
    { headers: authHeader() }
  );
}

/* ── History ─────────────────────────────────────────────────── */

/**
 * fetchJobHistory
 * ---------------
 * Returns a paginated list of all jobs (including cancelled and
 * inactive ones) filtered by stage and optional search criteria.
 *
 * Endpoint: GET /jobs/history
 * Params (all optional except stage):
 *   stage          {string}  – required; one of CURRENT_JOB_STAGES (incl. 'Cancelled')
 *   page           {integer} – page number (default 1)
 *   count          {integer} – results per page (default 20)
 *   site_id        {integer|null}
 *   title          {string|null}  – partial, case-insensitive
 *   type           {string|null}  – 'New Install' | 'Maintenance'
 *   priority       {integer|null} – 1–5
 *   is_active      {boolean|null}
 *   needs_training {boolean|null} – true/false filter on post-completion training requirement
 *   created_before {string|null}  – ISO 8601 datetime
 *   created_after  {string|null}  – ISO 8601 datetime
 *   updated_before {string|null}  – ISO 8601 datetime
 *   updated_after  {string|null}  – ISO 8601 datetime
 * Headers: Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   jobs: Job[],
 *   pagination: { page, shown_count, desired_count, total_pages, total_jobs }
 * }
 *
 * @param {Object} params – query parameters matching the schema above
 * @returns {Promise<AxiosResponse>}
 */
export function fetchJobHistory(params = {}) {
  /* Strip out null / undefined values so they are not sent as "null" strings */
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return axios.get(`${JOBS_BASE}/jobs/history`, { headers: authHeader(), params: cleaned });
}

/**
 * fetchJobData
 * ------------
 * Retrieves job data from the /jobs/data endpoint for use in frontend
 * services such as file export and data analysis.  Returns up to 100
 * items per call; callers should iterate over pages to retrieve the
 * full dataset.
 *
 * Endpoint: GET /jobs/data
 * Params:
 *   page           {integer, min:1}             – page number (default 1)
 *   count          {integer, min:1, max:100}    – items per page (default 20)
 *   stage          {string|null}                – stage filter
 *   type           {string|null}                – type filter
 *   site_id        {integer|null}               – site filter
 *   priority       {integer|null}               – priority filter
 *   needs_training {boolean|null}               – training requirement filter
 *   is_active      {boolean|null}               – active status filter
 *   created_before {string|null}                – ISO 8601 datetime upper bound
 *   created_after  {string|null}                – ISO 8601 datetime lower bound
 *   updated_before {string|null}                – ISO 8601 datetime upper bound
 *   updated_after  {string|null}                – ISO 8601 datetime lower bound
 * Headers: Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   data: JobDataItem[],
 *   pagination: { page, shown_count, desired_count, total_pages, total_jobs }
 * }
 *
 * @param {Object} params – query parameters matching the schema above
 * @returns {Promise<AxiosResponse>}
 */
export function fetchJobData(params = {}) {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return axios.get(`${JOBS_BASE}/jobs/data`, { headers: authHeader(), params: cleaned });
}

/* ── Notes ───────────────────────────────────────────────────── */

/**
 * fetchNotes
 * ----------
 * Returns all notes for a job.
 * The caller is responsible for sorting (typically newest-first
 * by created_at).
 *
 * Endpoint: GET /jobs/:id/notes
 * Params:   id {integer, minimum: 1} – the job's unique identifier
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   notes: [{
 *     id, job_id, user_id,
 *     note       {string}  – the note body text
 *     created_at {string, format: date-time},
 *     updated_at {string, format: date-time}
 *   }]
 * }
 *
 * @param {number} jobId
 * @returns {Promise<AxiosResponse>}
 */
export function fetchNotes(jobId) {
  return axios.get(
    `${JOBS_BASE}/jobs/${jobId}/notes`,
    { headers: authHeader() }
  );
}

/**
 * addNote
 * -------
 * Creates a new note on a job via PUT.  The API associates the note
 * with the currently authenticated user.
 *
 * Endpoint: PUT /jobs/:id/notes
 * Params:   id {integer, minimum: 1}
 * Payload:  { note {string} }   – the note body text (pre-trimmed)
 * Headers:  Authorization (Bearer)
 * Response: 201 { success: boolean, message: string, data: { id: integer } }
 *           (note list is re-fetched on success)
 *
 * @param {number} jobId
 * @param {string} note  – note content (should be pre-trimmed)
 * @returns {Promise<AxiosResponse>}
 */
export function addNote(jobId, note) {
  return axios.put(
    `${JOBS_BASE}/jobs/${jobId}/notes`,
    { note },
    { headers: authHeader() }
  );
}

/**
 * deleteNote
 * ----------
 * Permanently removes a note from a job.
 * The delete button is hidden in the UI for notes not belonging
 * to the current user, so this call should only ever be made by
 * the note's author.
 *
 * Endpoint: DELETE /jobs/:id/notes/:note_id
 * Headers:  Authorization (Bearer)
 * Response: { success: boolean, message: string }
 *
 * @param {number} jobId
 * @param {number} noteId
 * @returns {Promise<AxiosResponse>}
 */
export function deleteNote(jobId, noteId) {
  return axios.delete(
    `${JOBS_BASE}/jobs/${jobId}/notes/${noteId}`,
    { headers: authHeader() }
  );
}

/**
 * fetchLogs
 * ---------
 * Retrieves a paginated list of audit log entries for a job.
 *
 * Endpoint: GET /jobs/:id/logs
 * Params:   id    {integer, minimum: 1}
 * Query:    page  {integer, default: 1}  – page number
 *           count {integer, default: 20} – entries per page
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success:    boolean,
 *   message:    string,
 *   logs:       { id, job_id, user_id, log_type, log_message, created_at }[],
 *   pagination: { page, shown_count, desired_count, total_count, total_pages }
 * }
 *
 * @param {number} jobId
 * @param {number} [page=1]
 * @param {number} [count=20]
 * @returns {Promise<AxiosResponse>}
 */
export function fetchLogs(jobId, page = 1, count = 20) {
  return axios.get(
    `${JOBS_BASE}/jobs/${jobId}/logs`,
    { headers: authHeader(), params: { page, count } }
  );
}

/**
 * fetchAllLogs
 * ------------
 * Retrieves a paginated list of audit log entries across all jobs,
 * optionally filtered by job, site, log type, and date range.
 *
 * Endpoint: GET /jobs/logs
 * Query (all optional):
 *   page           {integer}  – page number (default 1)
 *   count          {integer}  – entries per page (default 20)
 *   job_id         {integer}  – filter to a specific job
 *   site_id        {integer}  – filter to a specific site
 *   job_log_type   {string}   – one of the values returned by fetchLogTypes
 *   created_before {string}   – ISO 8601 datetime upper bound
 *   created_after  {string}   – ISO 8601 datetime lower bound
 * Headers: Authorization (Bearer)
 * Response: {
 *   success:    boolean,
 *   message:    string,
 *   logs:       { id, job_id, log_type, log_data, log_message, created_at, created_by }[],
 *   pagination: { page, shown_count, desired_count, total_count, total_pages }
 * }
 *
 * @param {Object} params – query parameters matching the schema above
 * @returns {Promise<AxiosResponse>}
 */
export function fetchAllLogs(params = {}) {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
  return axios.get(`${JOBS_BASE}/jobs/logs`, { headers: authHeader(), params: cleaned });
}

/**
 * fetchLogTypes
 * -------------
 * Retrieves the list of possible job log type strings that can be
 * used as the `job_log_type` filter value in fetchAllLogs.
 *
 * Endpoint: GET /jobs/logs/types
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success:   boolean,
 *   message:   string,
 *   log_types: string[]
 * }
 *
 * @returns {Promise<AxiosResponse>}
 */
export function fetchLogTypes() {
  return axios.get(`${JOBS_BASE}/jobs/logs/types`, { headers: authHeader() });
}

/* ── Checklist Presets ───────────────────────────────────────────── */

/**
 * fetchChecklistPresets
 * ---------------------
 * Retrieves all checklist presets with their items.
 *
 * Endpoint: GET /jobs/checklist-presets
 * Headers:  Authorization (Bearer)
 * Response: {
 *   success: boolean,
 *   message: string,
 *   checklist_presets: [{
 *     id, name, description,
 *     checklist_items: [{ id, name, description, position, stage }]
 *   }]
 * }
 *
 * @returns {Promise<AxiosResponse>}
 */
export function fetchChecklistPresets() {
  return axios.get(`${JOBS_BASE}/jobs/checklist-presets`, { headers: authHeader() });
}

/**
 * createChecklistPreset
 * ---------------------
 * Creates a new checklist preset.
 *
 * Endpoint: PUT /jobs/checklist-presets
 * Payload:  { name {string} required, description {string} optional }
 * Response: { success: boolean, message: string }
 *
 * @param {{ name: string, description?: string }} payload
 * @returns {Promise<AxiosResponse>}
 */
export function createChecklistPreset(payload) {
  return axios.put(`${JOBS_BASE}/jobs/checklist-presets`, payload, { headers: authHeader() });
}

/**
 * updateChecklistPreset
 * ---------------------
 * Updates the name / description of an existing checklist preset.
 *
 * Endpoint: PATCH /jobs/checklist-presets/:presetId
 * Payload:  { name {string} required, description {string} optional }
 * Response: { success: boolean, message: string }
 *
 * @param {number} presetId
 * @param {{ name: string, description?: string }} payload
 * @returns {Promise<AxiosResponse>}
 */
export function updateChecklistPreset(presetId, payload) {
  return axios.patch(
    `${JOBS_BASE}/jobs/checklist-presets/${presetId}`,
    payload,
    { headers: authHeader() }
  );
}

/**
 * deleteChecklistPreset
 * ---------------------
 * Deletes a checklist preset.
 *
 * Endpoint: DELETE /jobs/checklist-presets/:presetId
 * Response: { success: boolean, message: string }
 *
 * @param {number} presetId
 * @returns {Promise<AxiosResponse>}
 */
export function deleteChecklistPreset(presetId) {
  return axios.delete(
    `${JOBS_BASE}/jobs/checklist-presets/${presetId}`,
    { headers: authHeader() }
  );
}

/**
 * addChecklistPresetItem
 * ----------------------
 * Adds a new item to a checklist preset.
 *
 * Endpoint: PUT /jobs/checklist-presets/:presetId/items
 * Payload:  { name, description?, position, stage? }
 * Response: { success: boolean, message: string }
 *
 * @param {number} presetId
 * @param {Object} payload
 * @returns {Promise<AxiosResponse>}
 */
export function addChecklistPresetItem(presetId, payload) {
  return axios.put(
    `${JOBS_BASE}/jobs/checklist-presets/${presetId}/items`,
    payload,
    { headers: authHeader() }
  );
}

/**
 * updateChecklistPresetItem
 * -------------------------
 * Updates an item within a checklist preset.
 *
 * Endpoint: PATCH /jobs/checklist-presets/:presetId/items/:itemId
 * Payload:  { name, description?, position, stage? }
 * Response: { success: boolean, message: string }
 *
 * @param {number} presetId
 * @param {number} itemId
 * @param {Object} payload
 * @returns {Promise<AxiosResponse>}
 */
export function updateChecklistPresetItem(presetId, itemId, payload) {
  return axios.patch(
    `${JOBS_BASE}/jobs/checklist-presets/${presetId}/items/${itemId}`,
    payload,
    { headers: authHeader() }
  );
}

/**
 * deleteChecklistPresetItem
 * -------------------------
 * Deletes an item from a checklist preset.
 *
 * Endpoint: DELETE /jobs/checklist-presets/:presetId/items/:itemId
 * Response: { success: boolean, message: string }
 *
 * @param {number} presetId
 * @param {number} itemId
 * @returns {Promise<AxiosResponse>}
 */
export function deleteChecklistPresetItem(presetId, itemId) {
  return axios.delete(
    `${JOBS_BASE}/jobs/checklist-presets/${presetId}/items/${itemId}`,
    { headers: authHeader() }
  );
}

/**
 * reorderChecklistPresetItems
 * ---------------------------
 * Reorders items within a checklist preset.
 *
 * Endpoint: PATCH /jobs/checklist-presets/:presetId/reorder-items
 * Payload:  { checklist_item_ids: [{ id, position }] }
 * Response: { success: boolean, message: string }
 *
 * @param {number} presetId
 * @param {Array<{ id: number, position: number }>} checklistItemIds
 * @returns {Promise<AxiosResponse>}
 */
export function reorderChecklistPresetItems(presetId, checklistItemIds) {
  return axios.patch(
    `${JOBS_BASE}/jobs/checklist-presets/${presetId}/reorder-items`,
    { checklist_item_ids: checklistItemIds },
    { headers: authHeader() }
  );
}
